import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

interface ProgressData {
  type: 'domain' | 'imap' | 'user';
  processed: number;
  total: number;
  percentage: number;
  current?: string;
  failed?: number;
}

interface JobStatus {
  jobId: string;
  status: 'started' | 'running' | 'complete' | 'stopped' | 'failed';
  workflowId?: string;
  message?: string;
  jobMode?: string; // 'setup' | 'dkim' | 'imap' | etc
}

interface WebSocketContextType {
  isConnected: boolean;
  lastMessage: any | null;
  sendMessage: (message: any) => void;
  progressData: ProgressData | null;
  jobStatus: JobStatus | null;
}

const WebSocketContext = createContext<WebSocketContextType>({
  isConnected: false,
  lastMessage: null,
  sendMessage: () => {},
  progressData: null,
  jobStatus: null,
});

export const useWebSocket = () => useContext(WebSocketContext);

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<any>(null);
  const [progressData, setProgressData] = useState<ProgressData | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const queryClient = useQueryClient();

  // Auto-clear completed/failed progress after 10 seconds
  useEffect(() => {
    if (jobStatus && (jobStatus.status === 'complete' || jobStatus.status === 'failed' || jobStatus.status === 'stopped')) {
      console.log(`Job ${jobStatus.status}, will clear progress in 10 seconds`);
      const timer = setTimeout(() => {
        console.log('Clearing stale progress data');
        setProgressData(null);
        setJobStatus(null);
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [jobStatus]);

  useEffect(() => {
    // Generate a unique client ID
    const clientId = `dashboard-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const handleMessage = (data: any) => {
      console.log('WebSocket message received:', data.type, data);
      setLastMessage(data);

      // Handle different message types
      switch (data.type) {
        case 'workflow_update':
          // Trigger workflow query refresh
          console.log('Workflow update received, invalidating workflows query');
          queryClient.invalidateQueries({ queryKey: ['workflows'] });
          break;
        case 'agent_status':
          // Update agent status
          break;
        case 'sms_verification':
          // Show SMS verification modal
          break;
        case 'job_status':
          // Handle job status from domain coordinator
          console.log('Job status:', data);
          if (data.status === 'completed') {
            setProgressData({
              type: 'domain',
              processed: data.completed_domains || data.total_domains || 0,
              total: data.total_domains || 0,
              percentage: data.progress_percentage || 100,
              current: 'Completed',
              failed: data.failed_domains || 0,
            });
            setJobStatus({
              jobId: data.job_id,
              status: 'complete',
              message: `${data.job_mode === 'dkim' ? 'DKIM' : data.job_mode === 'setup' ? 'Domain setup' : 'Job'} completed: ${data.completed_domains}/${data.total_domains} domains`,
              jobMode: data.job_mode,
            });
            // Auto-hide progress after showing completion (but keep connection open)
            setTimeout(() => {
              setProgressData(null);
            }, 5000);
          } else if (data.status === 'processing' || data.status === 'running') {
            const jobModeText = data.job_mode === 'dkim' ? 'DKIM configuration' : 
                               data.job_mode === 'setup' ? 'domain setup' : 
                               'domains';
            setProgressData({
              type: 'domain',
              processed: data.completed_domains || 0,
              total: data.total_domains || 0,
              percentage: data.progress_percentage || 0,
              current: `Processing ${jobModeText}...`,
              failed: data.failed_domains || 0,
            });
            setJobStatus({
              jobId: data.job_id,
              status: 'running',
              message: `${data.job_mode === 'dkim' ? 'DKIM configuration' : data.job_mode === 'setup' ? 'Domain setup' : 'Job'} in progress`,
              jobMode: data.job_mode,
            });
          }
          break;
        case 'job_started':
          console.log('Job started:', data);
          setJobStatus({
            jobId: data.job_id,
            status: 'started',
            workflowId: data.workflow_id,
            message: data.message || 'Job has started',
          });
          // Reset progress when new job starts
          setProgressData(null);
          break;
        case 'progress_update':
          console.log('Progress update:', data);
          const isDomainCoordinator = data.coordinator?.startsWith('domain');
          const isImapCoordinator = data.coordinator?.startsWith('imap');
          const progressType = isDomainCoordinator ? 'domain' :
                             isImapCoordinator ? 'imap' : 'user';

          // Handle domain coordinator format
          if (isDomainCoordinator) {
            // Determine job mode from the message
            const jobMode = data.job_mode || (data.job_id && data.job_id.includes('dkim') ? 'dkim' : 'setup');
            const jobModeText = jobMode === 'dkim' ? 'DKIM configuration' : 'domain setup';

            setProgressData({
              type: 'domain',
              processed: data.total_completed || data.completed_domains || 0,
              total: data.total_domains || 0,
              percentage: data.progress_percentage || 0,
              current: data.status === 'completed' ? 'Completed' : `Processing ${jobModeText}...`,
              failed: data.total_failed || data.failed_domains || 0,
            });

            // Always update job status for domain progress updates
            if (data.status === 'completed') {
              setJobStatus({
                jobId: data.job_id,
                status: 'complete',
                message: `${jobMode === 'dkim' ? 'DKIM' : 'Domain setup'} completed: ${data.total_completed}/${data.total_domains} domains`,
                jobMode: jobMode,
              });
            } else {
              // Set as running for any progress update (with or without status field)
              setJobStatus({
                jobId: data.job_id,
                status: 'running',
                message: `${jobMode === 'dkim' ? 'DKIM configuration' : 'Domain setup'} in progress: ${data.total_completed || 0}/${data.total_domains || 0}`,
                jobMode: jobMode,
              });
            }
          } else {
            // Handle other coordinators (IMAP, user creation)
            setProgressData({
              type: progressType,
              processed: data.processed || 0,
              total: data.total || 0,
              percentage: data.percentage || 0,
              current: data.current,
              failed: data.failed,
            });

            // Set job status for IMAP/user coordinators
            const coordinatorType = isImapCoordinator ? 'IMAP configuration' : 'User creation';
            setJobStatus({
              jobId: data.job_id || `${data.coordinator}-job`,
              status: 'running',
              message: `${coordinatorType} in progress: ${data.processed || 0}/${data.total || 0}`,
              jobMode: isImapCoordinator ? 'imap' : 'user',
            });
          }
          break;
        case 'job_complete':
        case 'job_completed':  // Handle both variants
          console.log('Job complete:', data);
          setJobStatus({
            jobId: data.job_id,
            status: 'complete',
            workflowId: data.workflow_id,
            message: data.message || 'Job completed successfully',
          });
          // Update progress to 100% before clearing
          if (progressData) {
            setProgressData({
              ...progressData,
              percentage: 100,
              processed: progressData.total,
            });
          }
          // Keep progress visible for a bit, but don't clear job status
          // as the next step in the workflow might start soon
          setTimeout(() => {
            setProgressData(null);
            // Only clear job status if it's still showing this completion
            setJobStatus(prev => 
              prev && prev.jobId === data.job_id ? null : prev
            );
          }, 5000);
          break;
        case 'job_stopped':
          console.log('Job stopped:', data);
          setJobStatus({
            jobId: data.job_id,
            status: 'stopped',
            workflowId: data.workflow_id,
            message: data.message || 'Job was stopped',
          });
          setProgressData(null);
          break;
        case 'job_failed':
          console.log('Job failed:', data);
          setJobStatus({
            jobId: data.job_id,
            status: 'failed',
            workflowId: data.workflow_id,
            message: data.error || 'Job failed',
          });
          setProgressData(null);
          break;
        case 'dkim_wait_started':
          console.log('DKIM wait period started:', data);
          // Clear progress display during wait period
          setProgressData(null);
          // The countdown timer in Workflows.tsx will handle displaying the wait
          break;
        default:
          console.log('Received WebSocket message:', data);
      }
    };

    // Connect to WebSocket
    const ws = api.connectWebSocket(clientId, handleMessage);
    wsRef.current = ws as any;

    const checkConnection = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        setIsConnected(true);
      } else {
        setIsConnected(false);
      }
    }, 1000);

    return () => {
      clearInterval(checkConnection);
      if (ws) {
        ws.close();
      }
    };
  }, []);

  const sendMessage = (message: any) => {
    if (wsRef.current) {
      wsRef.current.send(JSON.stringify(message));
    }
  };

  return (
    <WebSocketContext.Provider value={{ isConnected, lastMessage, sendMessage, progressData, jobStatus }}>
      {children}
    </WebSocketContext.Provider>
  );
};